import type { ChangeEvent } from 'react';

import { CloseIcon, SearchIcon } from '../../../icons';
import { IconButton } from '../../atoms/IconButton';
import { InputAdornment } from '../../atoms/InputAdornment';
import { TextField } from '../TextField';

const DEFAULT_CLEAR_LABEL = 'Effacer la recherche';

type Props = {
  value: string;
  onChange: (value: string) => void;
  /** Accessible name of the field, also used when no placeholder is given. */
  label: string;
  placeholder?: string;
  clearLabel?: string;
  size?: 'small' | 'medium';
  'data-testid'?: string;
};

export function SearchField(props: Props) {
  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    props.onChange(event.target.value);
  }

  function handleClear() {
    props.onChange('');
  }

  return (
    <TextField
      fullWidth
      size={props.size ?? 'small'}
      value={props.value}
      onChange={handleChange}
      placeholder={props.placeholder ?? props.label}
      data-testid={props['data-testid']}
      slotProps={{
        // `type="search"` would add the browser's own clear button next to ours.
        htmlInput: { 'aria-label': props.label, role: 'searchbox', enterKeyHint: 'search' },
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
          endAdornment: props.value ? (
            <InputAdornment position="end">
              <IconButton size="small" edge="end" tooltip={props.clearLabel ?? DEFAULT_CLEAR_LABEL} onClick={handleClear}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        },
      }}
    />
  );
}

export type { Props as SearchFieldProps };
